/**
 * AI Tutor API.
 *
 * - Lessons are ZIPs built in ai-tutor-v2; chapters and narration are served by
 *   the backend with signed URLs (never straight from the bucket).
 * - Doubts are answered by the tutor gateway on the mentor's laptop. Asking
 *   returns a job id at once; the answer is polled for. sendChatMessage and
 *   sendVoiceDoubt hide that: they resolve with the answer, as they always did.
 */

import api from "../../api";

// ─── Package APIs ────────────────────────────────────────────────────────────

/**
 * Get presigned URL for uploading a ZIP package
 * @param {number} topicId - Course topic ID
 * @param {string} title - Package title
 * @param {string} filename - Original filename
 * @returns {Promise<{upload_url: string, s3_key: string, package_id: number}>}
 */
export const getUploadUrl = async (topicId, title, filename) => {
  const response = await api.post(
    `ai-tutor/topic/${topicId}/packages/upload-url/`,
    { title, filename }
  );
  return response.data;
};

/**
 * Upload ZIP file directly to S3 using presigned URL
 * @param {string} uploadUrl - Presigned S3 URL
 * @param {File} file - ZIP file to upload
 * @returns {Promise<void>}
 */
export const uploadToS3 = async (uploadUrl, file) => {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": "application/zip",
    },
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
  }
};

/**
 * Trigger package processing after upload
 * @param {number} packageId - Package ID
 * @param {string} s3Key - S3 key of uploaded ZIP
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const processPackage = async (packageId, s3Key) => {
  const response = await api.post(`ai-tutor/packages/${packageId}/process/`, {
    s3_key: s3Key,
  });
  return response.data;
};

/**
 * Get list of packages for a topic
 * @param {number} topicId - Course topic ID
 * @returns {Promise<Array>}
 */
export const getPackages = async (topicId) => {
  const response = await api.get(`ai-tutor/topic/${topicId}/packages/`);
  return response.data;
};

/**
 * Get package details
 * @param {number} packageId - Package ID
 * @returns {Promise<Object>}
 */
export const getPackageDetail = async (packageId) => {
  const response = await api.get(`ai-tutor/packages/${packageId}/`);
  return response.data;
};

// ─── Session APIs ────────────────────────────────────────────────────────────

/**
 * Create a new learning session
 * @param {number} packageId - Package ID to learn from
 * @returns {Promise<{session: Object}>}
 */
export const createSession = async (packageId) => {
  const response = await api.post("ai-tutor/sessions/create/", {
    package_id: packageId,
  });
  return response.data;
};

/**
 * Get list of user's sessions
 * @returns {Promise<Array>}
 */
export const getSessions = async () => {
  const response = await api.get("ai-tutor/sessions/");
  return response.data;
};

/**
 * Get session details with package info and chat history
 * @param {string} sessionId - Session UUID
 * @returns {Promise<Object>}
 */
/** Turns of the conversation per page of history. */
export const CHAT_PAGE = 100;

const asChatMessage = (m, n) => ({
  // The tutor numbers every turn (`i`); messages from before it are numbered here.
  id: m.i !== null && m.i !== undefined ? `h-${m.i}` : `h-old-${n}`,
  i: m.i ?? null,
  role: m.role,
  content: m.content,
  chapter_index: m.chapter_index,
  timestamp: m.timestamp ? new Date(m.timestamp * 1000).toISOString() : null,
});

const oldestTurn = (messages) => {
  const numbered = messages.filter((m) => m.i !== null);
  return numbered.length ? numbered[0].i : null;
};

export const getSessionDetail = async (sessionId) => {
  // The conversation lives with the tutor now, not in the session record, so
  // it is fetched alongside: the latest page of it. A tutor that is offline
  // leaves the history empty rather than failing the page.
  const [detail, history] = await Promise.all([
    api.get(`ai-tutor/sessions/${sessionId}/`),
    api.get(`ai-tutor/sessions/${sessionId}/chat/`, { params: { paged: 1, limit: CHAT_PAGE } }).catch(() => null),
  ]);
  const data = detail.data;
  // A backend from before paging answers with a plain list.
  const page = Array.isArray(history?.data) ? { messages: history.data, has_more: false } : history?.data;
  if (page && Array.isArray(page.messages)) {
    data.chat_messages = page.messages.map(asChatMessage);
    data.chat_has_more = Boolean(page.has_more);
    data.chat_oldest_turn = oldestTurn(data.chat_messages);
  }
  return data;
};

/**
 * The page of conversation before turn `before` (the oldest one shown).
 * @returns {Promise<{messages: Array, hasMore: boolean, oldestTurn: number|null}>}
 */
export const getEarlierChat = async (sessionId, before) => {
  const r = await api.get(`ai-tutor/sessions/${sessionId}/chat/`, {
    params: { paged: 1, limit: CHAT_PAGE, before },
  });
  const messages = (r.data?.messages || []).map(asChatMessage);
  return { messages, hasMore: Boolean(r.data?.has_more), oldestTurn: oldestTurn(messages) };
};

/**
 * Mark a chapter as visited
 * @param {string} sessionId - Session UUID
 * @param {number} chapterIndex - Chapter index to mark as visited
 * @returns {Promise<{visited_chapters: Array, progress_percentage: number}>}
 */
export const visitChapter = async (sessionId, chapterIndex) => {
  const response = await api.post(
    `ai-tutor/sessions/${sessionId}/visit-chapter/`,
    { chapter_index: chapterIndex }
  );
  return response.data;
};

// ─── Content APIs ────────────────────────────────────────────────────────────

/** One chapter's HTML, media already pointed at signed URLs. */
export const getChapterHtml = async (sessionId, chapterIndex) => {
  const response = await api.get(`ai-tutor/sessions/${sessionId}/chapters/${chapterIndex}/`);
  return response.data;
};

/** {keys: [...], urls: {key: signedUrl|null}} for the narration of these texts. */
export const getNarrationUrls = async (sessionId, texts) => {
  const response = await api.post(`ai-tutor/sessions/${sessionId}/narration/`, { texts });
  return response.data;
};

/** The mentor's uploaded videos, with signed URLs. */
export const getVideos = async (sessionId) => {
  const response = await api.get(`ai-tutor/sessions/${sessionId}/videos/`);
  return response.data;
};

// ─── Tutor APIs ──────────────────────────────────────────────────────────────

/** {online, reason}: whether doubts can be answered right now. */
export const getTutorStatus = async (sessionId) => {
  const response = await api.get(`ai-tutor/sessions/${sessionId}/tutor-status/`);
  return response.data;
};


const POLL_START_MS = 1500;
const POLL_MAX_MS = 4000;
const POLL_GIVE_UP_MS = 6 * 60 * 1000;

const sleep = (ms, signal) =>
  new Promise((resolve) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => { clearTimeout(t); resolve(); }, { once: true });
  });

const aborted = () => {
  const err = new Error("aborted");
  err.name = "AbortError";
  return err;
};

const failure = (message, extra = {}) => {
  // The shape the slice already reads: rejectWithValue(error.response?.data).
  const err = new Error(message);
  err.response = { data: { error: message, ...extra } };
  return err;
};

const requestId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`)
    .replace(/[^A-Za-z0-9-]/g, "");

/**
 * Wait for a question's answer. `onProgress({state, position})` is told about
 * the queue, so the reader can say "2 questions ahead of you".
 */
const waitForAnswer = async (sessionId, jobId, onProgress, signal) => {
  const started = Date.now();
  let delay = POLL_START_MS;
  let misses = 0;
  for (;;) {
    // Stops when the reader closes: a poll that outlived the page used to
    // deliver its answer into whichever lesson was opened next.
    if (signal?.aborted) throw aborted();
    await sleep(delay, signal);
    if (signal?.aborted) throw aborted();
    let job;
    try {
      job = (await api.get(`ai-tutor/sessions/${sessionId}/chat/jobs/${jobId}/`)).data;
      misses = 0;
    } catch (e) {
      // A network blip is not a failed question: the job is still running on
      // the tutor. Only give up after it has been unreachable for a while.
      if (e.response && e.response.status < 500) throw e;
      if (++misses > 10) throw failure("Lost contact with the tutor. Your question may still be answered — reload in a minute.");
      continue;
    }
    if (onProgress) onProgress({ state: job.state, position: job.position || 0 });
    if (job.state === "done") return job;
    if (job.state === "failed") throw failure(job.error || "The tutor could not answer that.", job);
    // Finished so long ago the tutor let it go; its answer is in the history.
    if (job.state === "expired") throw failure(job.error || "This answer is in your conversation history.", job);
    if (Date.now() - started > POLL_GIVE_UP_MS) {
      throw failure("The tutor is taking unusually long. Your answer will appear here when you reload.");
    }
    delay = Math.min(POLL_MAX_MS, delay + 500);
  }
};

const asAnswer = (job, extra = {}) => ({
  response: job.answer,
  sources: job.sources || [],
  doubt_turns: job.doubt_turns,
  doubt_limit: job.doubt_limit,
  doubts_remaining: job.doubts_remaining,
  ...extra,
});

/**
 * Ask a doubt and resolve with the answer.
 * @returns {Promise<{response, sources, doubt_turns, doubt_limit, doubts_remaining}>}
 */
export const sendChatMessage = async (
  sessionId,
  message,
  chapterIndex = null,
  contextText = "",
  imageData = "",
  section = "",
  onProgress = null,
  signal = null,
  screen = {}
) => {
  const asked = await api.post(`ai-tutor/sessions/${sessionId}/chat/`, {
    message,
    chapter_index: chapterIndex,
    section,
    // What was on screen when they asked: the tutor's clue to "this".
    visible_sections: screen.visibleSections || [],
    highlight_section: screen.highlightSection || "",
    context_text: contextText,
    image_data: imageData,
    request_id: requestId(),
  });
  const job = await waitForAnswer(sessionId, asked.data.job_id, onProgress, signal);
  return asAnswer(job);
};

/**
 * A spoken doubt: transcribed by the backend, then answered like a typed one.
 * @returns {Promise<{transcript, response, sources, doubt_turns, doubt_limit, doubts_remaining}>}
 */
export const sendVoiceDoubt = async (
  sessionId,
  audioFile,
  chapterIndex = null,
  contextText = "",
  section = "",
  onProgress = null,
  signal = null,
  screen = {}
) => {
  const formData = new FormData();
  formData.append("audio", audioFile);
  if (chapterIndex !== null) formData.append("chapter_index", chapterIndex);
  if (contextText) formData.append("context_text", contextText);
  if (section) formData.append("section", section);
  if (screen.visibleSections?.length) formData.append("visible_sections", screen.visibleSections.join(","));
  if (screen.highlightSection) formData.append("highlight_section", screen.highlightSection);
  const asked = await api.post(`ai-tutor/sessions/${sessionId}/voice-doubt/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const job = await waitForAnswer(sessionId, asked.data.job_id, onProgress, signal);
  return asAnswer(job, { transcript: asked.data.transcript });
};

// ─── Health Check ────────────────────────────────────────────────────────────

/**
 * Check AI Tutor service health
 * @returns {Promise<{status: string, services: Object}>}
 */
export const checkHealth = async () => {
  const response = await api.get("ai-tutor/health/");
  return response.data;
};

// ─── The shared chat (workspace/Tutor/TutorTabs) on a lesson ─────────────────
//
// The lesson's chat endpoints answer in the AI Tutor's own shapes (a raw
// job_id, an error as a string); this adapter gives TutorTabs/TutorChat the
// shape they share with the book and session tutors.

const S = (sessionId) => `ai-tutor/sessions/${sessionId}`;

const asJob = (job) => ({
  ...job,
  // "expired": the answer is in the history already — the chat reloads it rather than waits.
  state: job.state === "expired" ? "failed" : job.state,
  error: job.error ? { message: job.error, code: job.error_code } : undefined,
});

/** TutorTabs' api for a lesson: the main conversation and its tabs. */
export const tutorChatApi = (sessionId) => ({
  ask: async (body) => {
    const d = (await api.post(`${S(sessionId)}/chat/`, body)).data;
    return { job: d.job_id, state: d.state, position: d.position || 0 };
  },
  job: async (jobId) => asJob((await api.get(`${S(sessionId)}/chat/jobs/${jobId}/`)).data),
  history: async (before = null) =>
    (await api.get(`${S(sessionId)}/chat/`, { params: { paged: 1, limit: CHAT_PAGE, ...(before !== null ? { before } : {}) } })).data,
  clear: async () => (await api.delete(`${S(sessionId)}/chat/`)).data,
  status: () => getTutorStatus(sessionId),
  threads: async () => (await api.get(`${S(sessionId)}/chat/threads/`)).data.threads,
  createThread: async (body) => (await api.post(`${S(sessionId)}/chat/threads/`, body)).data,
  updateThread: async (id, patch) => (await api.patch(`${S(sessionId)}/chat/threads/${id}/`, patch)).data,
  askThread: async (id, body) => {
    const d = (await api.post(`${S(sessionId)}/chat/threads/${id}/chat/`, body)).data;
    return { job: d.job_id, state: d.state, position: d.position || 0 };
  },
  threadHistory: async (id, before = null) =>
    (await api.get(`${S(sessionId)}/chat/threads/${id}/chat/`, { params: { limit: CHAT_PAGE, ...(before !== null ? { before } : {}) } })).data,
});
